import { useMemo } from 'react';
import { QueryDefinition } from '@/core';
import { useVizFrameworkConfig } from '@/plugin-system';
import { scanPanelsForVariableUsage, useTemplateVariableDefinitions } from '../TemplateVariableProvider';
import { useDashboardStore } from './DashboardProvider';
import { useListPanelGroups } from './dashboard-provider-api';

const EMPTY_QUERIES: string[] = [];

export interface PanelMetadata {
  panelKey: string;
  panelName: string;
  panelType: string;
  groupId: number;
  groupTitle?: string;
  layoutId: string;
  /** All query strings from the panel */
  queries: string[];
  variableUsage?: string[];
}

/**
 * Returns a list of all panels with their metadata including keys, names, types, queries, and group information.
 * Panels are ordered by their position in the dashboard (top-to-bottom, left-to-right within each group).
 */
export function useListPanelsWithMetadata({ includeVariableUsage = true }: { includeVariableUsage?: boolean } = {}) {
  const panels = useDashboardStore((state) => state.panels);
  const panelGroups = useListPanelGroups();
  const variableUsageMap = useVariableUsageMap({ includeVariableUsage });

  return useMemo(() => {
    const panelList: PanelMetadata[] = [];

    for (const group of panelGroups) {
      if (group.kind === 'Grid') {
        // Get layout items sorted by position (top-to-bottom, left-to-right)
        const sortedLayouts = [...group.itemLayouts].sort((a, b) => {
          // Sort by y position first (top to bottom)
          if (a.y !== b.y) {
            return a.y - b.y;
          }
          // Then by x position (left to right)
          return a.x - b.x;
        });
        for (const layout of sortedLayouts) {
          const panelKey = group.itemPanelKeys[layout.i];
          if (panelKey) {
            const panel = panels[panelKey];
            if (panel) {
              const queries = (panel.spec.queries ?? [])
                .map((q: QueryDefinition) => {
                  // ChronosphereDataQueryDefinition has spec.query for the query string
                  const plugin = q?.spec?.plugin;
                  if (!plugin || typeof plugin !== 'object') return undefined;
                  const spec = (plugin as { spec?: { query?: unknown } }).spec;
                  if (!spec) return undefined;
                  const query = spec.query;
                  return typeof query === 'string' ? query : undefined;
                })
                .filter((q): q is string => q !== undefined);

              const panelMetadata: PanelMetadata = {
                panelKey,
                panelName: panel.spec.display.name || 'Unnamed Panel',
                panelType: panel.spec.plugin.kind,
                groupId: group.id,
                groupTitle: group.title,
                layoutId: layout.i,
                queries,
              };

              // Only include variable usage if requested
              if (includeVariableUsage) {
                panelMetadata.variableUsage = variableUsageMap[panelKey] || [];
              }

              panelList.push(panelMetadata);
            }
          }
        }
      }
    }

    return panelList;
  }, [panels, panelGroups, variableUsageMap, includeVariableUsage]);
}

/**
 * Collects query strings from every panel on the dashboard except the given one.
 * Used by VariableOpportunitiesProvider to suggest template variables that
 * align with queries already in use across the dashboard.
 */
export function useOtherPanelQueries(panelKey: string | undefined) {
  const panels = useDashboardStore((state) => state.panels);
  const config = useVizFrameworkConfig();
  const enabled = config?.enableVariableOpportunities ?? false;

  return useMemo(() => {
    if (!enabled || panelKey === undefined) return EMPTY_QUERIES;
    const queries: string[] = [];
    for (const [key, panel] of Object.entries(panels)) {
      if (key === panelKey) continue;
      panel.spec.queries?.forEach((queryDef) => {
        const q = queryDef.spec.plugin.spec?.query;
        if (typeof q === 'string' && q) queries.push(q);
      });
    }
    return queries;
  }, [panels, panelKey, enabled]);
}

/**
 * Scan panels for variable usage.
 * Only performs scanning when includeVariableUsage is true to avoid unnecessary computation.
 */
function useVariableUsageMap({ includeVariableUsage = true }: { includeVariableUsage?: boolean } = {}) {
  const panels = useDashboardStore((state) => state.panels);
  const variables = useTemplateVariableDefinitions();
  return useMemo(() => {
    if (!includeVariableUsage) {
      return {};
    }
    const variableNames = variables.map((v) => v.spec.name);
    return scanPanelsForVariableUsage(panels, variableNames);
  }, [includeVariableUsage, panels, variables]);
}
